import { eventRaceFields, normalizeEventRacePriority } from '../utils/eventRace';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import type { CalendarEvent, EventSportType } from '../services/api';
import { forms } from '../services/api';
import { NmIsoDateField } from '../components/NmIsoDateField';
import { eventSportLabel } from '../utils/eventSportLabel';
import { toastErrorMessage } from '../context/ToastContext';
import './event-form.css';

type EventWorkoutType = EventSportType;

function ModalOverlay({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return createPortal(
    <div className="event-form-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {children}
    </div>,
    document.body,
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--fg-4)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '7px 10px', borderRadius: 'var(--r-2)',
  border: '1px solid var(--surface-sunken)', background: 'var(--surface-0)',
  color: 'var(--fg-1)', fontSize: 12, fontFamily: 'var(--font-body)',
  outline: 'none', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.08)',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle, cursor: 'pointer', appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%236b7280' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center',
  paddingRight: 28,
};

const btnBase: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 'var(--r-2)', border: 'none',
  cursor: 'pointer', fontSize: 12, fontWeight: 700,
  fontFamily: 'var(--font-body)', transition: 'opacity 0.12s',
};

/** Normalise API event_date to YYYY-MM-DD. */
function toEventDateISO(value: string | undefined | null): string {
  if (!value) return '';
  return String(value).split(/[T ]/)[0];
}

/** sport_type_id may be a number or nested sport type object from EventResource. */
function resolveSportTypeId(raw: CalendarEvent['sport_type_id']): number | '' {
  if (typeof raw === 'number') return raw;
  if (raw && typeof raw === 'object' && 'id' in raw) {
    const id = (raw as { id: number }).id;
    return typeof id === 'number' ? id : '';
  }
  return '';
}

/** Map a leaf or parent sport type id to parent + optional sub-type selects. */
function resolveSportTypeSelection(
  typeId: number | '',
  eventTypes: EventWorkoutType[],
): { parentId: number | ''; childId: number | '' } {
  if (typeId === '') return { parentId: '', childId: '' };

  for (const parent of eventTypes) {
    if (parent.id === typeId) {
      return { parentId: parent.id, childId: '' };
    }
    const child = parent.child?.find(c => c.id === typeId);
    if (child) {
      return { parentId: parent.id, childId: child.id };
    }
  }

  return { parentId: typeId, childId: '' };
}

const PRIORITY_OPTIONS = [
  { value: 1, code: 'A', color: 'var(--text-accent)', descKey: 'eventForm.priority.primary' as const },
  { value: 2, code: 'B', color: '#f59e0b', descKey: 'eventForm.priority.secondary' as const },
  { value: 3, code: 'C', color: '#9ca3af', descKey: 'eventForm.priority.training' as const },
] as const;

function PrioritySelector({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (v: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {PRIORITY_OPTIONS.map(opt => {
        const selected = value === opt.value;
        const desc = t(opt.descKey);
        return (
          <button
            key={opt.code}
            type="button"
            aria-pressed={selected}
            title={desc}
            onClick={() => onChange(opt.value)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              padding: '10px 8px',
              borderRadius: 10,
              border: `2px solid ${selected ? opt.color : 'var(--surface-sunken)'}`,
              background: selected ? `color-mix(in srgb, ${opt.color} 9%, transparent)` : 'var(--surface-sunken)',
              cursor: 'pointer',
              boxShadow: selected ? `0 0 0 1px color-mix(in srgb, ${opt.color} 25%, transparent)` : 'var(--nm-flat)',
              transition: 'all 0.12s',
            }}
          >
            <span style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: 14,
              color: opt.color,
              background: `color-mix(in srgb, ${opt.color} 13%, transparent)`,
              border: `1px solid color-mix(in srgb, ${opt.color} 33%, transparent)`,
            }}>
              {opt.code}
            </span>
            <span style={{ fontSize: 9, color: 'var(--fg-3)', fontWeight: 600 }}>{desc}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── EventFormModal ────────────────────────────────────────────────────────────

interface EventFormModalProps {
  date: string;
  userId: number;
  onSuccess: () => void;
  onClose: () => void;
  onDeleted?: () => void;
  /** Existing event — opens modal in edit mode. */
  event?: CalendarEvent | null;
  /** When true, user can pick the event date (e.g. ATP wizard). */
  editableDate?: boolean;
  minDate?: string;
  maxDate?: string;
  /** Show A / B / C race priority picker (ATP wizard). */
  showPriority?: boolean;
}

export function EventFormModal({
  date,
  userId,
  onSuccess,
  onClose,
  onDeleted,
  event = null,
  editableDate = false,
  minDate,
  maxDate,
  showPriority = false,
}: EventFormModalProps) {
  const { t, i18n } = useTranslation();
  const isEdit = event != null;
  const typeLabel = (type: EventSportType) => eventSportLabel(type, t, i18n.language);
  const [eventTypes, setEventTypes] = useState<EventWorkoutType[]>([]);

  const [eventDate, setEventDate] = useState(() => toEventDateISO(event?.event_date) || toEventDateISO(date));
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState<number | ''>('');
  const [selectedSubTypeId, setSelectedSubTypeId] = useState<number | ''>('');
  const [atpPriority, setAtpPriority] = useState<number | null>(null);
  const [isRace, setIsRace] = useState(showPriority);

  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesLoadFailed, setTypesLoadFailed] = useState(false);

  const selectedType = eventTypes.find(t => t.id === selectedTypeId);
  const subTypes = selectedType?.child ?? [];

  const finalTypeId = selectedSubTypeId !== '' ? selectedSubTypeId : selectedTypeId;
  const resolvedDate = editableDate || isEdit ? eventDate : date;

  useEffect(() => {
    let cancelled = false;
    forms.getEventTypes()
      .then(res => {
        if (cancelled) return;
        setEventTypes(res.data.data?.sportTypes ?? []);
      })
      .catch(() => {
        if (!cancelled) {
          setEventTypes([]);
          setTypesLoadFailed(true);
        }
      })
      .finally(() => { if (!cancelled) setTypesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (event) {
      setName(event.name);
      setDescription(event.description ?? '');
      setEventDate(toEventDateISO(event.event_date));
      setAtpPriority(normalizeEventRacePriority(event.atp_priority));
      setIsRace(showPriority || normalizeEventRacePriority(event.atp_priority) != null);
      return;
    }

    setName('');
    setDescription('');
    setEventDate(date);
    setAtpPriority(null);
    setIsRace(showPriority);
  }, [event, date, showPriority]);

  useEffect(() => {
    if (!eventTypes.length) return;
    if (event) {
      const { parentId, childId } = resolveSportTypeSelection(
        resolveSportTypeId(event.sport_type_id),
        eventTypes,
      );
      setSelectedTypeId(parentId || eventTypes[0].id);
      setSelectedSubTypeId(childId);
      return;
    }
    setSelectedTypeId(eventTypes[0].id);
    setSelectedSubTypeId('');
  }, [event, eventTypes]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (typesLoading || typesLoadFailed || !eventTypes.length) return;
    if (!name.trim()) { setError(t('eventForm.error.nameRequired')); return; }
    if (!finalTypeId) { setError(t('eventForm.error.typeRequired')); return; }
    if (!resolvedDate) { setError(t('eventForm.error.dateRequired')); return; }
    if (minDate && resolvedDate < minDate) {
      setError(t('eventForm.error.dateMin', { date: minDate }));
      return;
    }
    if (maxDate && resolvedDate > maxDate) {
      setError(t('eventForm.error.dateMax', { date: maxDate }));
      return;
    }
    if (isRace && atpPriority == null) {
      setError(t('eventForm.error.priorityRequired'));
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        name: name.trim(),
        event_date: `${resolvedDate} 00:00:00`,
        sport_type_id: finalTypeId,
        description: description.trim() || null,
      };
      Object.assign(payload, eventRaceFields(isRace, atpPriority));

      if (isEdit && event) {
        await forms.updateEvent(event.id, payload);
      } else {
        await forms.createEvent(userId, payload);
      }
      onSuccess();
    } catch (err) {
      setError(toastErrorMessage(err, isEdit ? t('eventForm.error.updateFailed') : t('eventForm.error.createFailed')));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!event?.id) return;
    if (!window.confirm(t('eventForm.action.deleteConfirm', { name: name.trim() || event.name }))) return;
    setDeleting(true);
    setError('');
    try {
      await forms.deleteEvent(event.id);
      onDeleted?.();
      onClose();
    } catch {
      setError(t('eventForm.error.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-surface event-form-dialog" role="dialog" aria-modal="true" aria-labelledby="event-form-title" style={{
        background: 'var(--surface-0)', borderRadius: 'var(--r-4)',
        boxShadow: 'var(--nm-raised)', width: 420, maxWidth: '95vw',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px 12px', borderBottom: '1px solid var(--surface-sunken)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--fg-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {isEdit
                ? (editableDate ? t('eventForm.headerEditLabel') : t('eventForm.headerEditWithDate', { date: toEventDateISO(event.event_date) }))
                : (editableDate ? t('eventForm.headerNewLabel') : t('eventForm.headerLabel', { date }))}
            </div>
            <div id="event-form-title" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-info, var(--fg-2))', fontFamily: 'var(--font-display)', marginTop: 2 }}>
              {isEdit ? t('eventForm.headerEditLabel') : t('eventForm.title')}
            </div>
          </div>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-4)', fontSize: 20, lineHeight: 1, padding: 4 }}
          >{t('common.close')}</button>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>

            {(editableDate || isEdit) && (
              <Field label={t('eventForm.field.eventDate')}>
                <NmIsoDateField
                  value={eventDate}
                  min={minDate}
                  max={maxDate}
                  onChange={setEventDate}

                  required
                  sheetZIndex={10100}
                />
              </Field>
            )}

            <Field label={t('eventForm.field.name')}>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t('eventForm.field.namePlaceholder')}
                required
                style={inputStyle}
              />
            </Field>

            {!showPriority && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px', border: '1px solid var(--border-strong)', borderRadius: 10, cursor: 'pointer', background: 'var(--surface-1)' }}>
                <input type="checkbox" checked={isRace} onChange={e => {
                  setIsRace(e.target.checked);
                  if (!e.target.checked) setAtpPriority(null);
                }} style={{ width: 20, height: 20, flexShrink: 0, accentColor: 'var(--palette-brand-ink)' }} />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <strong style={{ fontSize: 13, color: 'var(--fg-1)' }}>{t('eventForm.isRace')}</strong>
                  <span style={{ fontSize: 11, lineHeight: 1.7, color: 'var(--fg-3)' }}>{t('eventForm.isRaceHint')}</span>
                </span>
              </label>
            )}

            {isRace && (
              <Field label={t('eventForm.field.racePriority')}>
                <PrioritySelector value={atpPriority} onChange={setAtpPriority} />
              </Field>
            )}

            <Field label={t('eventForm.field.eventType')}>
              <select
                value={selectedTypeId}
                disabled={typesLoading || typesLoadFailed}
                onChange={e => {
                  setSelectedTypeId(Number(e.target.value));
                  setSelectedSubTypeId('');
                }}
                style={selectStyle}
              >
                {eventTypes.map(type => (
                  <option key={type.id} value={type.id}>{typeLabel(type)}</option>
                ))}
              </select>
            </Field>

            <Field label={t('eventForm.discipline')}>
              <select
                value={selectedSubTypeId}
                disabled={subTypes.length === 0}
                onChange={e => setSelectedSubTypeId(e.target.value ? Number(e.target.value) : '')}
                style={{ ...selectStyle, opacity: subTypes.length === 0 ? 0.65 : 1 }}
              >
                <option value="">{t('eventForm.field.subTypeNone')}</option>
                {subTypes.map(st => (
                  <option key={st.id} value={st.id}>{typeLabel(st)}</option>
                ))}
              </select>
            </Field>

            <Field label={t('eventForm.field.description')}>
              <textarea
                rows={3}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={t('eventForm.field.descriptionPlaceholder')}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
              />
            </Field>

            {(typesLoading || typesLoadFailed) && (
              <div role={typesLoadFailed ? 'alert' : 'status'} style={{ fontSize: 12, color: typesLoadFailed ? '#dc2626' : 'var(--fg-3)' }}>
                {typesLoading ? t('common.loading', 'Loading…') : t('eventForm.error.loadFailed', 'Failed to load event')}
              </div>
            )}

            {error && (
              <div style={{ color: '#dc2626', fontSize: 11, fontWeight: 600, padding: '6px 10px', background: '#fee2e2', borderRadius: 6 }}>
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '12px 20px', borderTop: '1px solid var(--surface-sunken)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap',
          }}>
            {isEdit ? (
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={submitting || deleting}
                style={{
                  ...btnBase,
                  background: 'rgba(232,93,117,0.12)',
                  color: '#E85D75',
                  border: '1px solid rgba(232,93,117,0.35)',
                  opacity: submitting || deleting ? 0.6 : 1,
                }}>
                {deleting ? t('eventForm.action.deleting') : t('eventForm.action.delete')}
              </button>
            ) : <span />}
            <div style={{ display: 'flex', gap: 8, marginInlineStart: 'auto' }}>
              <button type="button" onClick={onClose}
                style={{ ...btnBase, background: 'var(--surface-1)', color: 'var(--fg-2)' }}>
                {t('eventForm.action.cancel')}
              </button>
              <button type="submit" disabled={submitting || deleting || typesLoading || typesLoadFailed || !eventTypes.length}
                style={{ ...btnBase, background: '#087879', color: '#fff', opacity: submitting || deleting ? 0.7 : 1 }}>
                {submitting ? t('eventForm.action.saving') : isEdit ? t('eventForm.action.update') : t('eventForm.action.save')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </ModalOverlay>
  );
}
